/**
 * ZipEHR RM-type emojis on Blockly connection points.
 *
 * Block output type glyph: first field of HEADER, left of the title (next to
 * the output tab). Mutator cogwheel is last on that row (far right).
 * Value/statement slots: combined into FieldSlotLabel with the attr name
 * (see slot_label.ts). Abstract slots use ⁇ (underlined link), not a ring.
 * Specialization puzzles (PARTY_PROXY.KIND) still use this field alone.
 */
import type { Field, Input } from "blockly/core";
import { Blockly } from "./blockly_core.ts";
import { zipehrEmojiForRmType } from "../core/rm_emoji.ts";
import {
  attributesFor,
  baseRmTypeName,
  isAbstractType,
  resolveGenericSlotType,
  subtypesOf,
} from "../core/rm_meta.ts";

export const BLOCK_OUT_EMOJI_FIELD = "RM_OUT_EMOJI";
export const SLOT_EMOJI_FIELD_PREFIX = "SLOT_EMOJI_";
/** Double question mark (U+2047) — abstract slot marker, styled as a link in CSS. */
export const ABSTRACT_SLOT_GLYPH = "\u2047";

/** Theme body text is 12px; connection glyphs are ~50% larger. */
export const RM_EMOJI_FONT_PX = 18;
/** Document-like ZipEHR glyphs that stay illegible at 18px. */
export const RM_EMOJI_LARGE_FONT_PX = 24;

const HARD_TO_READ_RM_TYPES = new Set(["DV_TEXT", "DV_CODED_TEXT", "DV_PARAGRAPH"]);
const EMOJI_FONT =
  '"Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", "Google Sans", sans-serif';

export function slotEmojiFieldName(inputName: string): string {
  return `${SLOT_EMOJI_FIELD_PREFIX}${inputName}`;
}

/** RM class allowed in an attribute slot (unwraps List<> / generics). */
export function slotRmTypeForAttr(
  parentRmType: string,
  attrName: string,
  check?: string | string[] | null,
): string | undefined {
  const meta = attributesFor(parentRmType).find((a) => a.name === attrName);
  if (meta) return resolveGenericSlotType(parentRmType, meta.typeName);
  if (typeof check === "string") return check;
  if (Array.isArray(check)) {
    for (const t of check) {
      if (zipehrEmojiForRmType(t)) return t;
    }
    return check[0];
  }
  return undefined;
}

export function isHardToReadRmEmoji(rmType: string): boolean {
  return HARD_TO_READ_RM_TYPES.has(baseRmTypeName(rmType));
}

export function rmEmojiFontPx(rmType: string): number {
  return isHardToReadRmEmoji(rmType) ? RM_EMOJI_LARGE_FONT_PX : RM_EMOJI_FONT_PX;
}

/**
 * Glyph shown at a connection.
 * Slots of abstract types (PARTY_PROXY, CONTENT_ITEM, …) use ⁇
 * even when ZipEHR also has an emoji — the popup lists allowed subclasses.
 * Block output still prefers the ZipEHR emoji so the block keeps its identity.
 */
export function connectionPointGlyph(
  rmType: string | undefined,
  forSlot = false,
): string | undefined {
  if (!rmType) return undefined;
  if (forSlot && isAbstractPlaceholderType(rmType)) return ABSTRACT_SLOT_GLYPH;
  const emoji = zipehrEmojiForRmType(rmType);
  if (emoji) return emoji;
  if (isAbstractPlaceholderType(rmType)) return ABSTRACT_SLOT_GLYPH;
  return undefined;
}

export function isAbstractPlaceholderType(rmType: string): boolean {
  if (isAbstractType(rmType)) return true;
  if (zipehrEmojiForRmType(rmType)) return false;
  return subtypesOf(rmType, { concreteOnly: true }).length > 0;
}

/**
 * Hover text for connection-point glyphs and constraint-overlay tspans.
 * Overlay markers win so Δ/[n..m] do not show the abstract-type list.
 * Slot ⁇ / type glyphs win over the whole field group (the list used to
 * "move" onto the attribute name when data-rm-type-tip sat on fieldGroup).
 */
export function connectionPointHoverTip(host: Element | null | undefined): string {
  if (!host || typeof host.closest !== "function") return "";
  const overlay = host.closest("[data-constraint-overlay-tip]");
  if (overlay) return overlay.getAttribute("data-constraint-overlay-tip") ?? "";
  const glyph = host.closest(
    ".blockly-slot-abstract-glyph, .blockly-slot-type-glyph, .blockly-rm-emoji-field",
  );
  if (glyph) return glyph.getAttribute("data-rm-type-tip") ?? "";
  return host.getAttribute("data-rm-type-tip") ?? "";
}

/** Hover/click text: class name, or abstract type plus allowed concrete subclasses. */
export function rmTypeConnectionTooltip(rmType: string): string {
  if (!isAbstractPlaceholderType(rmType)) return rmType;
  const subs = subtypesOf(rmType, { concreteOnly: true }).slice().sort();
  const lines = [`${rmType} (abstract)`, "", "Allowed:"];
  for (const sub of subs) {
    lines.push(`${zipehrEmojiForRmType(sub) ?? ABSTRACT_SLOT_GLYPH} ${sub}`);
  }
  if (!subs.length) lines.push("(no concrete subclasses)");
  return lines.join("\n");
}

export function isRmTypeEmojiField(field: Field | null | undefined): field is FieldRmTypeEmoji {
  return Boolean(field && (field as FieldRmTypeEmoji).isRmTypeEmojiField);
}

// deno-lint-ignore no-explicit-any
const FieldLabelBase = Blockly.FieldLabel as any;

/** Connection-point ZipEHR glyph with tight layout, scaled typeface, and class-name tooltip. */
export class FieldRmTypeEmoji extends FieldLabelBase {
  readonly isRmTypeEmojiField = true;
  EDITABLE = false;
  SERIALIZABLE = false;
  CURSOR = "help";

  private rmType_ = "";
  private forSlot_ = false;

  constructor(rmType: string, forSlot = false, glyphOverride?: string) {
    const glyph = glyphOverride ?? connectionPointGlyph(rmType, forSlot) ?? "";
    super(glyph, cssClassFor(rmType, forSlot), { tooltip: rmTypeConnectionTooltip(rmType) });
    this.rmType_ = rmType;
    this.forSlot_ = forSlot;
    this.setTooltip(rmTypeConnectionTooltip(rmType));
    this.CURSOR = "help";
    installRmTypeEmojiTooltips();
  }

  rmType(): string {
    return this.rmType_;
  }

  setRmType(rmType: string): void {
    this.rmType_ = rmType;
    const glyph = connectionPointGlyph(rmType, this.forSlot_) ?? "";
    this.setClass(cssClassFor(rmType, this.forSlot_));
    this.CURSOR = "help";
    this.syncCssClasses_?.();
    this.setTooltip(rmTypeConnectionTooltip(rmType));
    this.setValue(glyph);
    this.syncTipAttr_();
    this.updateSize_?.();
  }

  initView(): void {
    super.initView();
    this.syncCssClasses_();
    this.syncTipAttr_();
    this.updateSize_();
  }

  render_(): void {
    super.render_?.();
    this.updateSize_();
  }

  /** Blockly would otherwise snap the glyph to FIELD_TEXT_BASELINE (12px header). */
  positionTextElement_(_xOffset?: number, _contentWidth?: number): void {
    this.layoutGlyph_();
  }

  getDisplayText_(): string {
    return super.getDisplayText_?.() ?? String(this.getText?.() ?? "");
  }

  updateSize_(): void {
    if (!this.size_) return;
    const px = rmEmojiFontPx(this.rmType_);
    const abstract = this.showsAbstractPlaceholder_();
    const text = String(this.getText?.() ?? this.getValue?.() ?? "");
    this.size_.width = abstract
      ? Math.ceil(px * 0.85)
      : measureGlyphWidth(text, px);
    this.size_.height = abstract ? Math.max(14, Math.round(px * 0.75)) : px;
    this.layoutGlyph_();
  }

  private showsAbstractPlaceholder_(): boolean {
    return this.forSlot_ && isAbstractPlaceholderType(this.rmType_);
  }

  private layoutGlyph_(): void {
    const px = rmEmojiFontPx(this.rmType_);
    const abstract = this.showsAbstractPlaceholder_();
    const el = this.textElement_ as SVGTextElement | null;
    if (!el) return;
    const fontPx = abstract ? Math.max(12, Math.round(px * 0.75)) : px;
    el.style.setProperty("font-size", `${fontPx}px`, "important");
    el.style.setProperty(
      "font-family",
      abstract ? '"Google Sans", "Segoe UI", sans-serif' : EMOJI_FONT,
      "important",
    );
    el.setAttribute("font-size", String(fontPx));
    el.setAttribute("dominant-baseline", "central");
    el.setAttribute("dy", "0");
    el.setAttribute("y", String(this.size_?.height ? this.size_.height / 2 : px / 2));
    el.setAttribute("text-anchor", "start");
    const left = this.glyphLeftBearing_();
    el.setAttribute("x", String(left));
  }

  private glyphLeftBearing_(): number {
    const px = rmEmojiFontPx(this.rmType_);
    const text = String(this.getText?.() ?? this.getValue?.() ?? "");
    return measureGlyphLeft(text, px);
  }

  private syncCssClasses_(): void {
    const group = this.fieldGroup_ as Element | null;
    const text = this.textElement_ as Element | null;
    const add = Blockly.utils?.dom?.addClass;
    const remove = Blockly.utils?.dom?.removeClass;
    if (group && add) {
      add(group, "blockly-rm-emoji-field");
      if (this.showsAbstractPlaceholder_()) add(group, "blockly-rm-emoji-abstract");
      else remove?.(group, "blockly-rm-emoji-abstract");
      if (isHardToReadRmEmoji(this.rmType_)) add(group, "blockly-rm-emoji-lg");
      else remove?.(group, "blockly-rm-emoji-lg");
    }
    if (text && add) {
      add(text, "blockly-rm-emoji");
      if (this.showsAbstractPlaceholder_()) add(text, "blockly-rm-emoji-abstract");
      else remove?.(text, "blockly-rm-emoji-abstract");
      if (isHardToReadRmEmoji(this.rmType_)) add(text, "blockly-rm-emoji-lg");
      else remove?.(text, "blockly-rm-emoji-lg");
    }
  }

  isClickableInFlyout(): boolean {
    return true;
  }

  private syncTipAttr_(): void {
    const tip = rmTypeConnectionTooltip(this.rmType_);
    this.fieldGroup_?.setAttribute("data-rm-type-tip", tip);
    const click = this.getClickTarget_?.();
    click?.setAttribute("data-rm-type-tip", tip);
  }
}

function cssClassFor(rmType: string, forSlot = false): string {
  const parts = ["blockly-rm-emoji"];
  if (isHardToReadRmEmoji(rmType)) parts.push("blockly-rm-emoji-lg");
  if (forSlot && isAbstractPlaceholderType(rmType)) parts.push("blockly-rm-emoji-abstract");
  return parts.join(" ");
}

let measureCanvas: HTMLCanvasElement | null = null;

function measureGlyphMetrics(text: string, fontPx: number): { width: number; left: number } {
  if (!text) return { width: 0, left: 0 };
  const fallbackWidth = Math.ceil(fontPx * 1.15 * Math.max(1, Array.from(text).length));
  if (typeof document !== "undefined") {
    try {
      measureCanvas ??= document.createElement("canvas");
      const ctx = measureCanvas.getContext("2d");
      if (ctx) {
        ctx.font = `${fontPx}px ${EMOJI_FONT}`;
        const m = ctx.measureText(text);
        const left = Math.max(0, Number(m.actualBoundingBoxLeft ?? 0));
        const right = Number(m.actualBoundingBoxRight ?? m.width);
        const ink = left + Math.max(right, 0);
        const width = Math.ceil(Math.max(m.width, ink, fontPx * 0.9) + left + 3);
        if (width > 0) return { width, left: Math.ceil(left) };
      }
    } catch {
      // Headless / canvas-less runtimes fall through.
    }
  }
  return { width: fallbackWidth, left: 1 };
}

/** Layout width for a connection glyph, including ink that would otherwise overflow. */
export function measureGlyphWidth(text: string, fontPx: number): number {
  return measureGlyphMetrics(text, fontPx).width;
}

function measureGlyphLeft(text: string, fontPx: number): number {
  return measureGlyphMetrics(text, fontPx).left;
}

export function createRmTypeEmojiField(
  rmType: string,
  forSlot = false,
): FieldRmTypeEmoji | null {
  if (!connectionPointGlyph(rmType, forSlot)) return null;
  return new FieldRmTypeEmoji(rmType, forSlot);
}

/** Type-fit glyph (✓, ☰, …) sized like ZipEHR connection emojis — not a 12px FieldLabel. */
export function createTypeGlyphField(glyph: string, tooltip: string): FieldRmTypeEmoji {
  const field = new FieldRmTypeEmoji("", false, glyph);
  field.setTooltip(tooltip);
  return field;
}

/** First field on HEADER — sits in the upper-left, next to output / previous-statement. */
export function appendBlockOutputEmoji(header: Input, rmType: string): void {
  const field = createRmTypeEmojiField(rmType, false);
  if (field) header.appendField(field, BLOCK_OUT_EMOJI_FIELD);
}

/**
 * Last field on a value or statement input so the glyph sits against the socket.
 * Updates in place when the allowed type changes (e.g. ELEMENT.value).
 */
export function appendSlotTypeEmoji(input: Input, rmType: string | undefined): void {
  const name = slotEmojiFieldName(input.name);
  const existing = input.fieldRow.find((f) => f.name === name);
  if (existing && isRmTypeEmojiField(existing)) {
    if (!rmType || !connectionPointGlyph(rmType, true)) {
      existing.setValue("");
      existing.setTooltip("");
      return;
    }
    existing.setRmType(rmType);
    return;
  }
  if (existing) {
    const glyph = connectionPointGlyph(rmType, true);
    existing.setValue(glyph ?? "");
    existing.setTooltip(rmType ? rmTypeConnectionTooltip(rmType) : "");
    return;
  }
  if (!rmType) return;
  const field = createRmTypeEmojiField(rmType, true);
  if (field) input.appendField(field, name);
}

let tooltipsInstalled = false;

export function installRmTypeEmojiTooltips(): void {
  const Tooltip = Blockly.Tooltip as
    | {
      HOVER_MS?: number;
      LIMIT?: number;
      setCustomTooltip?: (fn: (div: Element, el: Element) => void) => void;
      getTooltipOfObject?: (el: Element) => string;
      hide?: () => void;
    }
    | undefined;
  if (!Tooltip || tooltipsInstalled) return;
  tooltipsInstalled = true;
  try {
    Tooltip.LIMIT = 4000;
    Tooltip.HOVER_MS = 250;
  } catch {
    // Some builds export LIMIT / HOVER_MS as a const.
  }
  Tooltip.setCustomTooltip?.((div, el) => {
    const host = el as Element;
    const full = connectionPointHoverTip(host) ||
      Tooltip.getTooltipOfObject?.(el) ||
      "";
    const box = div as HTMLElement;
    box.style.whiteSpace = full.includes("\n") ? "pre-line" : "";
    box.textContent = full;
  });
}
